import type { FilterState, LandmarkFilter } from './types';
import { findSearchType } from '../../config/propertyTypeCatalog';

/**
 * URL ⇄ FilterState mapping for /search — extracted pure so defaults and
 * round-tripping are unit-testable.
 *
 * Product defaults (2026-07): listingType = 'rent', category = 'residential'.
 * Both are ALWAYS serialized so every internal link/share is canonical and
 * explicit — an old URL without params gets the defaults; "الكل" choices are
 * preserved via explicit sentinels (listingType=all, category=all).
 */
export const defaultFilters: FilterState = {
  query: '',
  minPrice: '',
  maxPrice: '',
  bedrooms: '',
  bathrooms: '',
  bedroomsMax: '',
  bathroomsMax: '',
  minArea: '',
  maxArea: '',
  propertyType: '',
  category: 'residential',
  listingType: 'rent',
  pricePeriod: 'yearly',
  petFriendly: false,
  parking: false,
  furnished: false,
  swimmingPool: false,
  gym: false,
  garden: false,
  elevator: false,
  security: false,
  landmark: null,
  radiusKm: undefined,
  areaId: null,
  cityId: null,
  cityName: null,
  locationLat: null,
  locationLng: null,
  locationRadiusKm: null,
};

// Location id / centroid / radius are handled separately (like areaId/cityName),
// so they are Omitted from the generic param map.
type ManagedFilterKey = keyof Omit<
  FilterState,
  'landmark' | 'radiusKm' | 'areaId' | 'cityId' | 'cityName' | 'locationLat' | 'locationLng' | 'locationRadiusKm'
>;

export const filterParamMap: Record<ManagedFilterKey, string> = {
  query: 'q',
  minPrice: 'minPrice',
  maxPrice: 'maxPrice',
  bedrooms: 'bedrooms',
  bathrooms: 'bathrooms',
  bedroomsMax: 'bedroomsMax',
  bathroomsMax: 'bathroomsMax',
  minArea: 'minArea',
  maxArea: 'maxArea',
  propertyType: 'type',
  category: 'category',
  listingType: 'listingType',
  pricePeriod: 'pricePeriod',
  petFriendly: 'petFriendly',
  parking: 'parking',
  furnished: 'furnished',
  swimmingPool: 'swimmingPool',
  gym: 'gym',
  garden: 'garden',
  elevator: 'elevator',
  security: 'security',
};

export const filterKeys = Object.keys(filterParamMap) as Array<ManagedFilterKey>;

const parseLandmarkFromParams = (params: URLSearchParams): LandmarkFilter | null => {
  const landmarkStr = params.get('landmark');
  if (!landmarkStr) return null;
  try {
    return JSON.parse(landmarkStr) as LandmarkFilter;
  } catch {
    return null;
  }
};

/** listingType: validated; absent/garbage → 'rent'; explicit 'all' honored. */
const parseListingType = (params: URLSearchParams): FilterState['listingType'] => {
  const raw = params.get(filterParamMap.listingType);
  return raw === 'all' || raw === 'rent' || raw === 'buy' ? raw : defaultFilters.listingType;
};

/**
 * category: explicit valid value wins ('all' sentinel → ''); otherwise derive
 * from the property type so a deep-link like ?type=OFFICE lands on the
 * commercial scope instead of contradicting the residential default.
 */
const parseCategory = (params: URLSearchParams, propertyType: string): FilterState['category'] => {
  const raw = params.get(filterParamMap.category);
  if (raw === 'residential' || raw === 'commercial') return raw;
  if (raw === 'all') return '';
  if (propertyType) return findSearchType(propertyType)?.searchCategory ?? defaultFilters.category;
  return defaultFilters.category;
};

export const createFiltersFromSearchParams = (params: URLSearchParams): FilterState => {
  // Normalize to the canonical UPPERCASE enum so deep-links from the nav
  // (e.g. ?type=VILLA) and the home search (?type=villa) both select the
  // matching filter chip and send a consistent value to the backend.
  const propertyType = ((params.get(filterParamMap.propertyType) ?? params.get('propertyType') ?? '') || '').toUpperCase();
  return {
    query: params.get(filterParamMap.query) ?? params.get('query') ?? defaultFilters.query,
    minPrice: params.get(filterParamMap.minPrice) ?? defaultFilters.minPrice,
    maxPrice: params.get(filterParamMap.maxPrice) ?? defaultFilters.maxPrice,
    bedrooms: params.get(filterParamMap.bedrooms) ?? defaultFilters.bedrooms,
    bathrooms: params.get(filterParamMap.bathrooms) ?? defaultFilters.bathrooms,
    bedroomsMax: params.get(filterParamMap.bedroomsMax) ?? defaultFilters.bedroomsMax,
    bathroomsMax: params.get(filterParamMap.bathroomsMax) ?? defaultFilters.bathroomsMax,
    minArea: params.get(filterParamMap.minArea) ?? defaultFilters.minArea,
    maxArea: params.get(filterParamMap.maxArea) ?? defaultFilters.maxArea,
    propertyType,
    category: parseCategory(params, propertyType),
    listingType: parseListingType(params),
    pricePeriod: params.get(filterParamMap.pricePeriod) === 'monthly' ? 'monthly' : defaultFilters.pricePeriod,
    petFriendly: (params.get(filterParamMap.petFriendly) ?? 'false') === 'true',
    parking: (params.get(filterParamMap.parking) ?? 'false') === 'true',
    furnished: (params.get(filterParamMap.furnished) ?? 'false') === 'true',
    swimmingPool: (params.get(filterParamMap.swimmingPool) ?? 'false') === 'true',
    gym: (params.get(filterParamMap.gym) ?? 'false') === 'true',
    garden: (params.get(filterParamMap.garden) ?? 'false') === 'true',
    elevator: (params.get(filterParamMap.elevator) ?? 'false') === 'true',
    security: (params.get(filterParamMap.security) ?? 'false') === 'true',
    landmark: parseLandmarkFromParams(params),
    radiusKm: params.get('radiusKm') ? parseInt(params.get('radiusKm')!) : undefined,
    areaId: params.get('areaId') ?? defaultFilters.areaId,
    cityId: params.get('cityId') ?? defaultFilters.cityId,
    cityName: params.get('city') ?? defaultFilters.cityName,
    locationLat: params.get('locLat') ? Number(params.get('locLat')) : defaultFilters.locationLat,
    locationLng: params.get('locLng') ? Number(params.get('locLng')) : defaultFilters.locationLng,
    locationRadiusKm: params.get('locRadius') ? Number(params.get('locRadius')) : defaultFilters.locationRadiusKm,
  };
};

export const serializeFilters = (filters: FilterState): URLSearchParams => {
  const params = new URLSearchParams();
  filterKeys.forEach((key) => {
    const value = filters[key];
    const paramKey = filterParamMap[key];
    if (typeof value === 'boolean') {
      if (value) {
        params.set(paramKey, 'true');
      }
    } else if (key === 'listingType') {
      // ALWAYS written (incl. 'all') — URLs must be canonical now that the
      // absent-param default is 'rent', or an explicit "All" would not survive
      // a reload/share.
      params.set(paramKey, value);
    } else if (key === 'pricePeriod') {
      // Only the non-default period is written, and only with a rent price, so a
      // plain URL stays plain.
      if (
        value !== defaultFilters.pricePeriod &&
        filters.listingType === 'rent' &&
        (filters.minPrice.trim() !== '' || filters.maxPrice.trim() !== '')
      ) {
        params.set(paramKey, value);
      }
    } else if (key === 'category') {
      // Same canonicalization: '' (all categories) serializes as the 'all'
      // sentinel rather than disappearing and re-defaulting to residential.
      params.set(paramKey, value === '' ? 'all' : value);
    } else if (value.trim() !== '') {
      params.set(paramKey, value);
    }
  });
  // Handle landmark and radiusKm separately
  if (filters.landmark) {
    params.set('landmark', JSON.stringify(filters.landmark));
  }
  if (filters.radiusKm !== undefined) {
    params.set('radiusKm', filters.radiusKm.toString());
  }
  if (filters.areaId) {
    params.set('areaId', filters.areaId);
  }
  if (filters.cityId) {
    params.set('cityId', filters.cityId);
  }
  if (filters.cityName) {
    params.set('city', filters.cityName);
  }
  if (filters.locationLat != null) {
    params.set('locLat', String(filters.locationLat));
  }
  if (filters.locationLng != null) {
    params.set('locLng', String(filters.locationLng));
  }
  if (filters.locationRadiusKm != null) {
    params.set('locRadius', String(filters.locationRadiusKm));
  }
  return params;
};

export const areFiltersEqual = (a: FilterState, b: FilterState): boolean => {
  const basicKeysEqual = filterKeys.every((key) => a[key] === b[key]);
  const landmarkEqual = JSON.stringify(a.landmark) === JSON.stringify(b.landmark);
  const radiusEqual = a.radiusKm === b.radiusKm;
  const areaEqual = (a.areaId ?? null) === (b.areaId ?? null);
  const cityIdEqual = (a.cityId ?? null) === (b.cityId ?? null);
  const cityEqual = (a.cityName ?? null) === (b.cityName ?? null);
  const locEqual =
    (a.locationLat ?? null) === (b.locationLat ?? null) &&
    (a.locationLng ?? null) === (b.locationLng ?? null) &&
    (a.locationRadiusKm ?? null) === (b.locationRadiusKm ?? null);
  return basicKeysEqual && landmarkEqual && radiusEqual && areaEqual && cityIdEqual && cityEqual && locEqual;
};
